import type {
  AccountantInboxAcksPage,
  AccountantInboxBoletosPage,
  AccountantInboxComposite,
  AccountantInboxObligationsPage,
  AccountantTenantDetail,
  BulkAckAcceptRequest,
  BulkAckAcceptResponse,
  BulkObligationMarkPaidRequest,
  BulkObligationMarkPaidResponse,
  DashboardResponse,
  EstadoFiscalFinancial,
  ExchangeRatesResponse,
  IvaHistoryResponse,
  IvaPeriodDetail,
  SubdiarioDetail,
  SubdiarioSummary,
} from "@fiscalo/shared-types";

import { apiClient } from "@/services/apiClient";

export function getDashboard(): Promise<DashboardResponse> {
  return apiClient
    .get<DashboardResponse>("/dashboard")
    .then((res) => res.data);
}

export function dismissDashboardAlert(alertId: string): Promise<{ ok: true }> {
  return apiClient
    .post<{ ok: true }>("/dashboard/dismissed-alerts", { alertId })
    .then((res) => res.data);
}

export function triggerPendingJobs(): Promise<{
  scheduled: number;
  enqueued: number;
  invoiceCount: number;
}> {
  return apiClient
    .post<{
      scheduled: number;
      enqueued: number;
      invoiceCount: number;
    }>("/dashboard/trigger-pending-jobs")
    .then((res) => res.data);
}

export function getExchangeRates(date?: string): Promise<ExchangeRatesResponse> {
  return apiClient
    .get<ExchangeRatesResponse>("/dashboard/exchange-rates", {
      params: date ? { date } : undefined,
    })
    .then((res) => res.data);
}

export function getEstadoFiscalFinancial(): Promise<EstadoFiscalFinancial> {
  return apiClient
    .get<EstadoFiscalFinancial>("/estado-fiscal")
    .then((res) => res.data);
}

export function getIvaHistory(
  from: string,
  to: string
): Promise<IvaHistoryResponse> {
  return apiClient
    .get<IvaHistoryResponse>("/estado-fiscal/iva-history", {
      params: { from, to },
    })
    .then((res) => res.data);
}

/**
 * Read-only tenant detail rendered in the accountant cartera flow
 * BEFORE the accountant clicks "Acceder como cliente". Backed by a
 * membership-gated endpoint, so the requesting user has to have a
 * TenantMembership on `tenantId`.
 */
export function getAccountantTenantDetail(
  tenantId: string,
): Promise<AccountantTenantDetail> {
  return apiClient
    .get<AccountantTenantDetail>(
      `/dashboard/accountant/tenants/${tenantId}/details`,
    )
    .then((res) => res.data);
}

/**
 * Dar de baja (offboard) a client tenant in the accountant's cartera.
 * Membership-gated on the backend — the accountant must have a
 * TenantMembership on `tenantId`. Stops reportes/recibidas/emisión and
 * pauses billing; data is retained.
 */
export function darDeBajaAccountantClient(
  tenantId: string,
  reason: string,
): Promise<{ id: string; bajaAt: string | null; bajaReason: string | null }> {
  return apiClient
    .post<{ id: string; bajaAt: string | null; bajaReason: string | null }>(
      `/dashboard/accountant/tenants/${tenantId}/baja`,
      { reason },
    )
    .then((res) => res.data);
}

/** Reactivar a previously dado-de-baja client tenant in the accountant's cartera. */
export function reactivarAccountantClient(
  tenantId: string,
): Promise<{ id: string; bajaAt: string | null }> {
  return apiClient
    .post<{ id: string; bajaAt: string | null }>(
      `/dashboard/accountant/tenants/${tenantId}/reactivar`,
      {},
    )
    .then((res) => res.data);
}

/** Trailing-N-month IVA history for a tenant in the accountant's cartera. */
export function getAccountantTenantIvaHistory(
  tenantId: string,
  months = 12,
): Promise<IvaHistoryResponse> {
  return apiClient
    .get<IvaHistoryResponse>(
      `/dashboard/accountant/tenants/${tenantId}/iva-history`,
      { params: { months } },
    )
    .then((res) => res.data);
}

/**
 * Bandeja composite — top 5 of each section + unbounded counts. Drives
 * the cartera-home preview AND the /accountant/inbox landing page.
 */
export function getAccountantInbox(): Promise<AccountantInboxComposite> {
  return apiClient
    .get<AccountantInboxComposite>("/dashboard/accountant/inbox")
    .then((res) => res.data);
}

/** Full pending-ACK list for /accountant/inbox/acks. */
export function getAccountantInboxAcks(
  opts: { limit?: number; tenantId?: string } = {},
): Promise<AccountantInboxAcksPage> {
  return apiClient
    .get<AccountantInboxAcksPage>("/dashboard/accountant/inbox/acks", {
      params: {
        ...(opts.limit ? { limit: opts.limit } : {}),
        ...(opts.tenantId ? { tenant: opts.tenantId } : {}),
      },
    })
    .then((res) => res.data);
}

/** Full pending-boletos list for /accountant/inbox/boletos. */
export function getAccountantInboxBoletos(
  opts: { limit?: number; tenantId?: string } = {},
): Promise<AccountantInboxBoletosPage> {
  return apiClient
    .get<AccountantInboxBoletosPage>("/dashboard/accountant/inbox/boletos", {
      params: {
        ...(opts.limit ? { limit: opts.limit } : {}),
        ...(opts.tenantId ? { tenant: opts.tenantId } : {}),
      },
    })
    .then((res) => res.data);
}

/** Full upcoming-obligations list for /accountant/inbox/obligations. */
export function getAccountantInboxObligations(
  opts: { limit?: number; days?: number; tenantId?: string } = {},
): Promise<AccountantInboxObligationsPage> {
  return apiClient
    .get<AccountantInboxObligationsPage>(
      "/dashboard/accountant/inbox/obligations",
      {
        params: {
          ...(opts.limit ? { limit: opts.limit } : {}),
          ...(opts.days ? { days: opts.days } : {}),
          ...(opts.tenantId ? { tenant: opts.tenantId } : {}),
        },
      },
    )
    .then((res) => res.data);
}

/* ---------- V2 action endpoints ---------- */

export interface AccountantRecibidaDecisionResult {
  estado: "AE" | "RE";
  decidedAt: string;
  alreadyDecided: boolean;
}

export function accountantAcceptRecibida(
  tenantId: string,
  recibidaId: string,
): Promise<AccountantRecibidaDecisionResult> {
  return apiClient
    .post<AccountantRecibidaDecisionResult>(
      `/dashboard/accountant/tenants/${tenantId}/recibidas/${recibidaId}/accept`,
    )
    .then((res) => res.data);
}

/**
 * Bulk-accept N pending ACK CFEs in one round trip. Each item carries
 * its own tenantId because the bandeja queue is cross-tenant — see the
 * controller doc for partial-success semantics.
 */
export function accountantBulkAcceptRecibidas(
  body: BulkAckAcceptRequest,
): Promise<BulkAckAcceptResponse> {
  return apiClient
    .post<BulkAckAcceptResponse>(
      `/dashboard/accountant/recibidas/bulk-accept`,
      body,
    )
    .then((res) => res.data);
}

/**
 * Accept every still-pending received CFE for one tenant in a single call.
 * Backs the "Aceptar todas" action on the cartera detail's pending-ACK alert
 * — the server enumerates the rows (same filter as the count), so no IDs are
 * sent. Accept-only; rejection stays per-document in the recibidas detail.
 */
export function accountantAcceptAllPendingRecibidas(
  tenantId: string,
): Promise<{ acceptedCount: number; alreadyCount: number; failedCount: number }> {
  return apiClient
    .post<{ acceptedCount: number; alreadyCount: number; failedCount: number }>(
      `/dashboard/accountant/tenants/${tenantId}/recibidas/accept-all-pending`,
    )
    .then((res) => res.data);
}

/**
 * Mark one rejected CFE's rechazo alert as handled for a cartera client.
 * Per-invoice on purpose — there is no bulk counterpart on the accountant
 * surface, so a genuinely-unsent CFE can't be cleared along with the noise.
 */
export function accountantDismissInvoiceRejection(
  tenantId: string,
  invoiceId: string,
): Promise<{ dismissedAt: string }> {
  return apiClient
    .post<{ dismissedAt: string }>(
      `/dashboard/accountant/tenants/${tenantId}/invoices/${invoiceId}/dismiss-rejection`,
    )
    .then((res) => res.data);
}

export function accountantRejectRecibida(
  tenantId: string,
  recibidaId: string,
  motivos: Array<{ code: string; description: string; detail?: string }>,
): Promise<AccountantRecibidaDecisionResult> {
  return apiClient
    .post<AccountantRecibidaDecisionResult>(
      `/dashboard/accountant/tenants/${tenantId}/recibidas/${recibidaId}/reject`,
      { motivos },
    )
    .then((res) => res.data);
}

export function accountantMarkObligationPaid(
  tenantId: string,
  obligationId: string,
  amountPaidUyu: number | null,
): Promise<{ ok: true }> {
  return apiClient
    .patch<{ ok: true }>(
      `/dashboard/accountant/tenants/${tenantId}/obligations/${obligationId}/amount-paid`,
      { amountPaidUyu },
    )
    .then((res) => res.data);
}

/**
 * Mark an obligation completed (paid) for a tenant in the cartera. Flips
 * status→completed (stops the agenda reminders), unlike amount-paid which only
 * records a figure. amountPaidUyu is optional — IVA passes the period's IVA,
 * BPS passes null.
 */
export function accountantCompleteObligation(
  tenantId: string,
  obligationId: string,
  amountPaidUyu: number | null,
): Promise<{ ok: true }> {
  return apiClient
    .patch<{ ok: true }>(
      `/dashboard/accountant/tenants/${tenantId}/obligations/${obligationId}/complete`,
      { amountPaidUyu },
    )
    .then((res) => res.data);
}

/**
 * Bulk mark N obligations as paid in one round trip. The shared
 * `amountPaidUyu` applies to every item — see the controller doc for the
 * cross-tenant + partial-success semantics.
 */
export function accountantBulkMarkObligationsPaid(
  body: BulkObligationMarkPaidRequest,
): Promise<BulkObligationMarkPaidResponse> {
  return apiClient
    .post<BulkObligationMarkPaidResponse>(
      `/dashboard/accountant/obligations/bulk-mark-paid`,
      body,
    )
    .then((res) => res.data);
}

/**
 * Download the XMLs of a hand-picked, cross-tenant set of received CFEs
 * as one ZIP. Backs the bandeja's bulk "Exportar XML" action.
 */
export function accountantExportRecibidasZip(
  items: BulkAckAcceptRequest["items"],
): Promise<{ blob: Blob; filename: string }> {
  return apiClient
    .post(
      `/dashboard/accountant/recibidas/export-zip`,
      { items },
      { responseType: "blob" },
    )
    .then((res) => {
      const disposition = res.headers["content-disposition"] ?? "";
      const match = /filename="?([^"]+)"?/.exec(disposition);
      return {
        blob: res.data as Blob,
        filename: match?.[1] ?? "CFE Recibidos Bandeja.zip",
      };
    });
}

export function accountantUploadBoleto(
  tenantId: string,
  obligationId: string,
  payload: { boletoNumber: string; boletoFileUrl: string },
): Promise<{ ok: true }> {
  return apiClient
    .patch<{ ok: true }>(
      `/dashboard/accountant/tenants/${tenantId}/obligations/${obligationId}/boleto`,
      payload,
    )
    .then((res) => res.data);
}

export function accountantConfirmRecurringOccurrence(
  tenantId: string,
  occurrenceId: string,
): Promise<{ ok: true }> {
  return apiClient
    .post<{ ok: true }>(
      `/dashboard/accountant/tenants/${tenantId}/recurring-occurrences/${occurrenceId}/confirm`,
    )
    .then((res) => res.data);
}

/* ---------- Subdiario de Ventas ---------- */

/** Trailing-12-month sales summary for the tenant detail page (all UYU). */
export function getAccountantSubdiarioSummary(
  tenantId: string,
): Promise<SubdiarioSummary> {
  return apiClient
    .get<SubdiarioSummary>(
      `/dashboard/accountant/tenants/${tenantId}/subdiario/summary`,
    )
    .then((res) => res.data);
}

/** CFEs behind one month's IVA — débito (ventas) + crédito (compras). */
export function getAccountantIvaPeriodDetail(
  tenantId: string,
  period: string,
): Promise<IvaPeriodDetail> {
  return apiClient
    .get<IvaPeriodDetail>(
      `/dashboard/accountant/tenants/${tenantId}/iva-period`,
      { params: { period } },
    )
    .then((res) => res.data);
}

/** Kick off DGI boleto-de-pago generation for a tenant's IVA obligation.
 *  The DGI-automation tool auto-solves the CAPTCHA; poll the session for the
 *  result. */
export function startAccountantBoleto(
  tenantId: string,
  obligationId: string,
): Promise<{ sessionId: string }> {
  return apiClient
    .post<{ sessionId: string }>(
      `/dashboard/accountant/tenants/${tenantId}/obligations/${obligationId}/boleto/start`,
    )
    .then((res) => res.data);
}

export function getAccountantBoletoSession(
  sessionId: string,
): Promise<{ status: string | null; currentStep: string | null }> {
  return apiClient
    .get<{ status: string | null; currentStep: string | null }>(
      `/dashboard/accountant/boleto-session/${sessionId}`,
    )
    .then((res) => res.data);
}

/** Full per-CFE sales ledger for one month (`period` = 'YYYY-MM'). */
export function getAccountantSubdiarioDetail(
  tenantId: string,
  period: string,
): Promise<SubdiarioDetail> {
  return apiClient
    .get<SubdiarioDetail>(
      `/dashboard/accountant/tenants/${tenantId}/subdiario`,
      { params: { period } },
    )
    .then((res) => res.data);
}

/** Streams the monthly ledger as a server-generated file and triggers a
 *  browser download. `format` selects csv or pdf. */
async function downloadAccountantSubdiario(
  tenantId: string,
  period: string,
  format: "csv" | "pdf",
): Promise<void> {
  const res = await apiClient.get(
    `/dashboard/accountant/tenants/${tenantId}/subdiario/${format}`,
    { params: { period }, responseType: "blob" },
  );
  const disposition = res.headers["content-disposition"] ?? "";
  const match = /filename="?([^"]+)"?/.exec(disposition);
  const filename = match?.[1] ?? `Subdiario Ventas ${period}.${format}`;
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function downloadAccountantSubdiarioCsv(
  tenantId: string,
  period: string,
): Promise<void> {
  return downloadAccountantSubdiario(tenantId, period, "csv");
}

export function downloadAccountantSubdiarioPdf(
  tenantId: string,
  period: string,
): Promise<void> {
  return downloadAccountantSubdiario(tenantId, period, "pdf");
}
